/**
 * MONSTER TOWN — INTERACTION RUNTIME. OWNED BY THE INTERIORS CREW.
 *
 * `src/app/interactions.ts` (architect) decides WHAT a tap means and fans it out
 * onto the bus. This folder is the part a kid can SEE: props animate, particles
 * fly. One install line in main.ts turns LAW #1 from a type-system promise into
 * something on screen.
 */

import { engine } from '../core/engine'
import { installPropAnimation } from './anim'
import {
  installFx,
  setFxQuality,
  burst as burstFx,
  liveParticleCount as liveCount,
  effectNames as listEffects,
} from './fx'
import { installSpawn } from './spawn'
import { installTapMark, tapMarkState } from './tapmark'
import type { GameContext } from '../app/context'

export { burst, effectNames, liveParticleCount } from './fx'
export { clipNames, activeAnimCount } from './anim'
export { spawnableNames, spawnedCount } from './spawn'
export { tapMarkState } from './tapmark'

export function installInteractionRuntime(ctx: GameContext): void {
  installPropAnimation()
  installFx()
  installSpawn()
  // The one reaction that does not depend on what a def declared: the thing you
  // tapped is marked from the frame you tap it, for the whole walk over to it.
  installTapMark()
  setFxQuality(ctx.renderer.qualityTier)

  // DEV-ONLY probe so the perf harness can stress particles and prop animation
  // without faking taps. Stripped from the production bundle by Vite.
  if (import.meta.env.DEV) {
    ;(window as unknown as Record<string, unknown>)['__mtFx'] = {
      burst: (effect: string, at: { x: number; y: number; z: number }, count?: number) =>
        burstFx(effect, at, count ?? 8),
      live: liveCount,
      effects: listEffects,
      tapMark: tapMarkState,
    }
  }

  // The renderer lowers quality before it lowers frame rate; particle counts
  // follow it down. Checked twice a second, not per frame.
  let tick = 0
  engine.addSystem({
    id: 'interactions.quality',
    order: 78,
    fixed: () => {
      tick++
      if (tick % 15 !== 0) return
      setFxQuality(ctx.renderer.qualityTier)
    },
  })
}
